import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { list } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { StatusPill, PriorityPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Printer, MapPin, Clock, Truck, Package, CalendarClock } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtTime = (v) => (v ? String(v).slice(11, 16) || "—" : "—");
const dayOf = (v) => (v ? String(v).slice(0, 10) : null);

export default function DriverSchedules() {
  const { data: reqs = [] } = useQuery({ queryKey: ["requisitions"], queryFn: () => list("requisitions") });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: () => list("drivers") });
  const [driverId, setDriverId] = useState("");
  const [date, setDate] = useState(todayISO());

  const driver = drivers.find((d) => d.id === driverId);
  const jobs = useMemo(() => {
    if (!driverId) return [];
    return reqs
      .filter((r) => r.driver_id === driverId)
      .filter((r) => dayOf(r.estimated_start) === date || dayOf(r.requested_pickup_date) === date)
      .sort((a, b) => String(a.estimated_start || "").localeCompare(String(b.estimated_start || "")));
  }, [reqs, driverId, date]);

  return (
    <Layout>
      <PageHeader title="Driver Schedules" subtitle="Daily driver run sheet with associated work orders — printable" noPrint>
        <Button size="sm" variant="outline" data-testid="print-schedule-btn" disabled={!driverId}
          onClick={() => window.print()} className="gap-2 border-border/60">
          <Printer className="h-4 w-4" /> Print
        </Button>
      </PageHeader>

      <div className="p-8 space-y-6 animate-fade-up">
        <Card className="p-4 border-border/60 shadow-none no-print">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Driver</Label>
              <div className="mt-1.5">
                <Select value={driverId} onValueChange={setDriverId}>
                  <SelectTrigger className="h-9" data-testid="schedule-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id} data-testid={`schedule-driver-opt-${d.id}`}>
                        {d.driver_name} {d.employee_id ? `(#${d.employee_id})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</Label>
              <Input type="date" data-testid="schedule-date-input" className="h-9 mt-1.5"
                value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
        </Card>

        {!driverId ? (
          <div className="text-center py-16 text-muted-foreground no-print">
            <CalendarClock className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
            Select a driver to view their daily schedule.
          </div>
        ) : (
          <div className="print-area bg-white rounded-md border border-border/60 p-6 max-w-4xl" data-testid="driver-schedule-sheet">
            <div className="flex items-start justify-between border-b border-neutral-900 pb-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Ghost TMS · Driver Run Sheet</div>
                <h2 className="font-display font-extrabold text-2xl leading-tight mt-1">{driver?.driver_name}</h2>
                <div className="text-sm text-neutral-600">
                  {driver?.employee_id && <>Emp #{driver.employee_id} · </>}
                  {driver?.cdl_class && <>CDL {driver.cdl_class} · </>}
                  {driver?.phone}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-neutral-500">Schedule Date</div>
                <div className="font-display font-bold text-lg">{date}</div>
                <div className="text-sm text-neutral-600 mt-1">{jobs.length} job{jobs.length === 1 ? "" : "s"}</div>
              </div>
            </div>

            {jobs.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground" data-testid="no-jobs">No jobs scheduled for this driver on {date}.</div>
            ) : (
              <div className="space-y-4">
                {jobs.map((r, i) => (
                  <div key={r.id} data-testid={`schedule-job-${r.id}`} className="rounded-md border border-border/60 p-4 break-inside-avoid">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="grid place-items-center h-6 w-6 rounded-full bg-neutral-900 text-white text-xs font-bold">{i + 1}</span>
                        <span className="font-mono-data font-semibold text-[#0055FF]">{r.work_order_no || r.requisition_no}</span>
                        <span className="text-sm font-medium">{r.project_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <PriorityPill value={r.priority} />
                        <StatusPill value={r.status} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-neutral-700 pl-8">
                      <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-neutral-400" />{fmtTime(r.estimated_start)} – {fmtTime(r.estimated_end)}{r.estimated_time_hrs ? ` (${r.estimated_time_hrs} hr)` : ""}</div>
                      <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-neutral-400" />{[r.truck_type, r.truck_unit_number].filter(Boolean).join(" #") || "—"}{r.trailer_number ? ` + ${r.trailer_type} #${r.trailer_number}` : ""}</div>
                      <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="h-4 w-4 text-neutral-400" />{r.pickup_location || "?"} → {r.delivery_location || "?"}</div>
                      <div className="flex items-start gap-2 sm:col-span-2"><Package className="h-4 w-4 text-neutral-400 mt-0.5" /><span>{r.load_description || "—"}{r.load_dimensions ? ` · ${r.load_dimensions}` : ""}{r.estimated_weight ? ` · ${r.estimated_weight}` : ""}</span></div>
                      {r.special_instructions && <div className="sm:col-span-2 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-800 ml-6">⚠ {r.special_instructions}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-border/60 grid grid-cols-2 gap-8 text-xs text-neutral-500">
              <div>Driver signature: __________________________</div>
              <div>Dispatcher: __________________________</div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
